import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../utils/api-client';

// Types
export interface BackfillRequest {
  organization_id?: string;
  batch_size: number;
  limit?: number;
}

export interface BackfillResponse {
  status: string;
  task_id: string;
  message: string;
  parameters: {
    organization_id?: string;
    batch_size: number;
    limit?: number;
  };
  task_url: string;
  estimated_completion?: string;
}

export interface SystemStats {
  total_signals: number;
  signals_with_taxonomy: number;
  root_cause_signals_count: number;
  organizations_count: number;
  backfill_coverage: number;
  last_backfill?: string;
}

export interface TaskStatus {
  task_id: string;
  status: string;
  result?: any;
  error?: string;
  progress?: any;
  created_at?: string;
  completed_at?: string;
}

export interface TasksOverview {
  workers: {
    registered: string[];
    count: number;
  };
  tasks: {
    active: number;
    scheduled: number;
    reserved: number;
    queued: number;
  };
  queues: Record<string, number>;
  active_tasks_by_worker: Record<string, any[]>;
  worker_stats: Record<string, any>;
}

// Custom Hooks

/**
 * Hook to get system statistics
 */
export const useSystemStats = () => {
  return useQuery<SystemStats>({
    queryKey: ['admin', 'system', 'stats'],
    queryFn: async () => {
      const response = await apiClient.get('/v1/admin/system/stats');
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

/**
 * Hook to get tasks overview
 */
export const useTasksOverview = () => {
  return useQuery<TasksOverview>({
    queryKey: ['admin', 'tasks', 'overview'],
    queryFn: async () => {
      const response = await apiClient.get('/v1/admin/tasks');
      return response.data;
    },
    refetchInterval: 5000, // Refresh every 5 seconds
  });
};

/**
 * Hook to get task status
 */
export const useTaskStatus = (taskId: string | null, enabled = true) => {
  return useQuery<TaskStatus>({
    queryKey: ['admin', 'task', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('Task ID is required');
      const response = await apiClient.get(`/v1/admin/task/${taskId}`);
      return response.data;
    },
    enabled: enabled && !!taskId,
    refetchInterval: (query) => {
      // Stop refetching if task is completed or failed
      if (query.state.data?.status === 'completed' || query.state.data?.status === 'failed') {
        return false;
      }
      return 2000; // Refresh every 2 seconds for active tasks
    },
  });
};

/**
 * Hook to start root cause backfill
 */
export const useStartBackfill = () => {
  const queryClient = useQueryClient();

  return useMutation<BackfillResponse, Error, BackfillRequest>({
    mutationFn: async (request) => {
      const response = await apiClient.post('/v1/admin/backfill/root-causes', request);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'system', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'overview'] });
    },
  });
};

/**
 * Hook to refresh continuous aggregates
 */
export const useRefreshAggregates = () => {
  const queryClient = useQueryClient();

  return useMutation<{ status: string; task_id: string; message: string; task_url: string }, Error>({
    mutationFn: async () => {
      const response = await apiClient.post('/v1/admin/system/refresh-aggregates', {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'overview'] });
    },
  });
};

/**
 * Hook to cancel a task
 */
export const useCancelTask = () => {
  const queryClient = useQueryClient();

  return useMutation<{ status: string; task_id: string; message: string }, Error, string>({
    mutationFn: async (taskId) => {
      const response = await apiClient.post(`/v1/admin/tasks/${taskId}/cancel`, {});
      return response.data;
    },
    onSuccess: (_, taskId) => {
      // Invalidate the specific task and overview
      queryClient.invalidateQueries({ queryKey: ['admin', 'task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'overview'] });
    },
  });
};

/**
 * Hook to delete all root causes
 */
export const useDeleteAllRootCauses = () => {
  const queryClient = useQueryClient();

  return useMutation<{ 
    status: string; 
    organization_id: string; 
    root_causes_deleted: number; 
    root_cause_signals_deleted: number; 
    message: string 
  }, Error>({
    mutationFn: async () => {
      const response = await apiClient.delete('/v1/root-causes/all?confirm=true');
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'system', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['root-causes'] });
    },
  });
};

/**
 * Hook to purge all root causes across every organization (admin-only)
 */
export const usePurgeAllRootCauses = () => {
  const queryClient = useQueryClient();

  return useMutation<{
    status: string;
    organization_id: string;
    root_causes_deleted: number;
    root_cause_signals_deleted: number;
    message: string;
  }, Error>({
    mutationFn: async () => {
      const response = await apiClient.delete('/v1/admin/purge-root-causes?confirm=true');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'system', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['root-causes'] });
    },
  });
};